import type { ContentRegistry } from '../content/registry';
import type { ItemDef } from '../content/types';
import { characterDef, characterId, type CharacterId } from '../content/characters';
import { assignedPetBonuses, PET_BY_ID } from '../content/pets';
import { assignedPets, characterLevel, countUnlocks, equippedItems, petLevel, skillPointsForLevel, unlockedUpgradeTags } from '../meta/profile';
import { resolveSkillBuild } from '../content/skills';
import type { SaveData } from '../meta/save';
import type { RunConfig } from '../sim/sim';
import type { CoopLoadout, LobbySettings } from './protocol';

/**
 * Online co-op run configuration. Every player brings their own profile —
 * character, equipment, pet, meta upgrades, upgrade-pool unlocks and reroll/
 * banish charges — and the host folds them into one deterministic RunConfig
 * (the per-player `*ByPlayer` fields). Start era and Paradox come from the
 * host's own progression. Pure functions; no DOM, no network.
 */

export const MAX_COOP_PLAYERS = 4;
const MAX_NAME = 24;

/** Snapshot of the local profile to send to the host. */
export function loadoutFromSave(save: SaveData, reg: ContentRegistry, name: string): CoopLoadout {
  const character = characterDef(save.selectedCharacter).id;
  const level = characterLevel(save.characters[character].xp);
  const build = resolveSkillBuild(character, save.characters[character].allocated, skillPointsForLevel(level));
  const housePets = assignedPets(save);
  const houseBonus = assignedPetBonuses(housePets);
  return {
    name: cleanName(name),
    character,
    characterLevel: level,
    skillAllocations: build.allocated,
    skillFlags: build.flags,
    equipment: equippedItems(save, reg).map((item) => item.id),
    pet: save.pets.active,
    petLevel: save.pets.active ? petLevel(save.pets.xp[save.pets.active] ?? 0) : 1,
    housePets,
    startingHeals: Math.max(0, Math.min(4, (save.house.rooms.garden ?? 0) + houseBonus.startingHeals)),
    metaMods: [...build.mods, ...houseBonus.mods],
    unlocked: [...unlockedUpgradeTags(save, reg)],
    rerolls: countUnlocks(save, reg, 'reroll') + Math.max(0, Math.min(3, save.house.rooms.chronolab ?? 0)) + houseBonus.rerolls,
    banishes: countUnlocks(save, reg, 'banish') + houseBonus.banishes,
  };
}

export function cleanName(name: unknown): string {
  const text = typeof name === 'string' ? name.replace(/[\u0000-\u001f<>]/g, '').trim() : '';
  return (text || 'Traveler').slice(0, MAX_NAME);
}

const int = (v: unknown, lo: number, hi: number) => (Number.isInteger(v) ? Math.max(lo, Math.min(hi, v as number)) : lo);

/**
 * Normalize a loadout received from the network. Friends are trusted not to
 * forge their profile (there are no co-op leaderboards), but the host must
 * never build a config that crashes or desyncs the sim, so unknown ids are
 * dropped and every number is bounded.
 */
export function sanitizeLoadout(value: unknown, reg: ContentRegistry): CoopLoadout | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const equipment: string[] = [];
  const slots = new Set<string>();
  for (const id of Array.isArray(v.equipment) ? v.equipment.slice(0, 6) : []) {
    const item = typeof id === 'string' ? reg.items.get(id) : undefined;
    if (!item || slots.has(item.slot)) continue;
    slots.add(item.slot);
    equipment.push(item.id);
  }
  const character = characterId(v.character);
  const level = int(v.characterLevel, 1, 30);
  const raw = v.skillAllocations && typeof v.skillAllocations === 'object' && !Array.isArray(v.skillAllocations) ? v.skillAllocations as Record<string, number> : {};
  const build = resolveSkillBuild(character, raw, skillPointsForLevel(level));
  const rawPositive: Record<string, number> = {};
  for (const [id, rank] of Object.entries(raw)) {
    if (Number(rank) <= 0) continue;
    if (!Number.isInteger(rank) || id.length > 64) return null;
    rawPositive[id] = rank;
  }
  const rawIds = Object.keys(rawPositive);
  if (rawIds.length !== Object.keys(build.allocated).length || rawIds.some((id) => build.allocated[id] !== rawPositive[id])) return null;
  const pet = typeof v.pet === 'string' && PET_BY_ID.has(v.pet) ? v.pet : null;
  const housePets = [...new Set((Array.isArray(v.housePets) ? v.housePets : []).filter((id): id is string => typeof id === 'string' && PET_BY_ID.has(id) && id !== pet))].slice(0, 3);
  const houseBonus = assignedPetBonuses(housePets);
  return {
    name: cleanName(v.name),
    character,
    characterLevel: level,
    skillAllocations: build.allocated,
    skillFlags: build.flags,
    equipment,
    pet,
    petLevel: int(v.petLevel, 1, 10),
    housePets,
    startingHeals: int(v.startingHeals, 0, 4),
    metaMods: [...build.mods, ...houseBonus.mods],
    unlocked: (Array.isArray(v.unlocked) ? v.unlocked : []).filter((t): t is string => typeof t === 'string' && t.length <= 64).slice(0, 64),
    rerolls: int(v.rerolls, 0, 20),
    banishes: int(v.banishes, 0, 20),
  };
}

function itemsFor(reg: ContentRegistry, ids: string[]): ItemDef[] {
  const items = ids.map((id) => reg.items.get(id)).filter((item): item is ItemDef => item !== undefined);
  if (!items.some((item) => item.slot === 'weapon')) {
    const starter = reg.items.get('w_spore_launcher');
    if (starter) items.unshift(starter);
  }
  return items;
}

/** Clamp the host's requested start era / Paradox to the host's own progression. */
export function clampCoopSettings(save: SaveData, reg: ContentRegistry, settings: LobbySettings): LobbySettings {
  const maxAnchor = Math.max(0, ...save.anchors.map((a) => a + 1));
  let startEra = Math.max(0, Math.min(int(settings.startEra, 0, reg.eras.length - 1), maxAnchor));
  while (startEra > 0 && !reg.eras[startEra]?.implemented) startEra--;
  return { startEra, paradox: Math.min(int(settings.paradox, 0, 99), save.paradoxUnlocked) };
}

/** Player order = roster order (host is always player 0). */
export function buildCoopRunConfig(reg: ContentRegistry, loadouts: readonly CoopLoadout[], settings: LobbySettings, seed: number): RunConfig {
  if (loadouts.length < 1 || loadouts.length > MAX_COOP_PLAYERS) throw new Error(`co-op needs 1–${MAX_COOP_PLAYERS} players`);
  const host = loadouts[0];
  return {
    seed: seed >>> 0,
    playerCount: loadouts.length,
    startEra: settings.startEra,
    paradox: settings.paradox,
    metaMods: host.metaMods,
    equipment: loadouts.map((l) => itemsFor(reg, l.equipment)),
    characters: loadouts.map((l) => characterId(l.character) as CharacterId),
    rerolls: host.rerolls,
    banishes: host.banishes,
    unlocked: new Set(host.unlocked),
    pets: loadouts.map((l) => l.pet),
    petLevels: loadouts.map((l) => l.petLevel ?? 1),
    startingHeals: loadouts.map((l) => l.startingHeals ?? 0),
    skillFlags: loadouts.map((l) => l.skillFlags ?? []),
    metaModsByPlayer: loadouts.map((l) => l.metaMods),
    unlockedByPlayer: loadouts.map((l) => new Set(l.unlocked)),
    rerollsByPlayer: loadouts.map((l) => l.rerolls),
    banishesByPlayer: loadouts.map((l) => l.banishes),
  };
}
